import {
    useState
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    Progress,
    Button,
    Text,
    Item
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    EllipsisIcon,
    FileTextIcon,
    FolderIcon,
    BellIcon
} from "lucide-react-native";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    createKitIcon
} from "../../components/kitIcons";
import coverImage from "../../assets/ncore-logo-preview.png";

const NAME = "Item";

const coverSource = {
    uri: coverImage
};

const FolderKitIcon = createKitIcon(FolderIcon);

const FileKitIcon = createKitIcon(FileTextIcon);

const BellKitIcon = createKitIcon(BellIcon);

const MoreKitIcon = createKitIcon(EllipsisIcon);

const USAGE_CODE = `<Item.Group
    variant="outline"
    isDivided={true}
>
    <Item>
        <Item.Media
            icon={FolderIcon}
            variant="icon"
        />
        <Item.Content>
            <Item.Title>
                Reports folder
            </Item.Title>
            <Item.Description>
                12 files · Grace Hopper updated yesterday
            </Item.Description>
        </Item.Content>
        <Item.Actions>
            <Button
                accessibilityLabel="More"
                onPress={handleMore}
                variant="outline"
                icon={MoreIcon}
            />
        </Item.Actions>
    </Item>
    <Item>
        <Item.Media
            source={coverImage}
            variant="image"
            alt="Cover"
        />
        <Item.Content>
            <Item.Title>
                cover-image.png
            </Item.Title>
            <Item.Description>
                1.2 MB
            </Item.Description>
        </Item.Content>
        <Item.Actions>
            <Button
                onPress={handleDownload}
                title="Download"
            />
        </Item.Actions>
    </Item>
    <Item>
        <Item.Media
            icon={FileIcon}
            variant="icon"
        />
        <Item.Content>
            <Item.Title>
                annual-report.pdf
            </Item.Title>
            <Item.Description>
                Uploading · 4.1 / 6.8 MB
            </Item.Description>
        </Item.Content>
        <Text>
            60%
        </Text>
        <Item.Footer>
            <Progress
                aria-label="annual-report.pdf"
                value={60}
                isValueVisible={false}
            />
        </Item.Footer>
    </Item>
</Item.Group>`;

const VARIANT_CODE = `<Item
    variant="plain"
>
    ...
</Item>

<Item
    variant="outline"
>
    ...
</Item>

<Item
    variant="filled"
>
    ...
</Item>`;

const SIZE_CODE = `<Item
    size="medium"
>
    ...
</Item>

<Item
    size="small"
>
    ...
</Item>

<Item
    size="xSmall"
>
    ...
</Item>`;

const MEDIA_CODE = `<Item.Media
    icon={BellIcon}
/>

<Item.Media
    variant="icon"
    icon={BellIcon}
/>

<Item.Media
    source={coverImage}
    variant="image"
    alt="Cover"
/>

<Item.Media
    avatarProps={{
        title: "Ada Lovelace"
    }}
    variant="avatar"
/>`;

const CONTENT_CODE = `<Item>
    <Item.Media
        variant="icon"
        icon={BellIcon}
    />
    <Item.Content>
        <Item.Title>
            Notifications
        </Item.Title>
        <Item.Description>
            Choose what reaches you and when.
        </Item.Description>
    </Item.Content>
    <Item.Actions>
        <Button
            onPress={handleOpen}
            title="Open"
        />
    </Item.Actions>
</Item>`;

const HEADER_CODE = `<Item
    variant="outline"
>
    <Item.Header>
        <Text>
            Shared with you
        </Text>
    </Item.Header>
    <Item.Content>
        <Item.Title>
            Budget 2027
        </Item.Title>
    </Item.Content>
    <Item.Footer>
        <Progress
            aria-label="Budget 2027"
            value={60}
            isValueVisible={false}
        />
    </Item.Footer>
</Item>`;

const GROUP_CODE = `<Item.Group
    variant="outline"
    isDivided={true}
>
    <Item>
        ...
    </Item>
    <Item>
        ...
    </Item>
</Item.Group>

<Item.Separator
    inset={72}
/>`;

const PRESS_CODE = `<Item
    onPress={() => setSelected("profile")}
    isSelected={selected === "profile"}
    aria-label="Profile"
>
    <Item.Content>
        <Item.Title>
            Profile
        </Item.Title>
    </Item.Content>
</Item>`;

const VARIANTS = [
    "plain",
    "outline",
    "filled"
] as const;

const SIZES = [
    "medium",
    "small",
    "xSmall"
] as const;

const ROWS = [
    "profile",
    "billing",
    "security"
] as const;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("itemPage.variantTitle"),
        id: "variant"
    },
    {
        title: t("itemPage.sizeTitle"),
        id: "size"
    },
    {
        title: t("itemPage.mediaTitle"),
        id: "media"
    },
    {
        title: t("itemPage.contentTitle"),
        id: "content"
    },
    {
        title: t("itemPage.headerTitle"),
        id: "header"
    },
    {
        title: t("itemPage.groupTitle"),
        id: "group"
    },
    {
        title: t("itemPage.pressTitle"),
        id: "press"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const ItemPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        selectedKey,
        setSelectedKey
    ] = useState<string>("profile");

    const handleNothing = () => undefined;

    const renderSimple = (variant: typeof VARIANTS[number], size: typeof SIZES[number]) => <Item
        key={`${variant}-${size}`}
        variant={variant}
        size={size}
    >
        <Item.Media
            icon={BellKitIcon}
        />
        <Item.Content>
            <Item.Title>
                {t("itemPage.notifications")}
            </Item.Title>
            <Item.Description>
                {t("itemPage.notificationsText")}
            </Item.Description>
        </Item.Content>
    </Item>;

    return <Article>
        <ComponentHeader
            lead={t("itemPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("itemPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("itemPage.overviewNote")}
            </Paragraph>
            <Preview
                caption={t("itemPage.overviewCaption")}
                isStacked={true}
            >
                <Item.Group
                    variant="outline"
                    isDivided={true}
                >
                    <Item>
                        <Item.Media
                            icon={FolderKitIcon}
                            variant="icon"
                        />
                        <Item.Content>
                            <Item.Title>
                                {t("itemPage.folder")}
                            </Item.Title>
                            <Item.Description>
                                {t("itemPage.folderText")}
                            </Item.Description>
                        </Item.Content>
                        <Item.Actions>
                            <Button
                                accessibilityLabel={t("itemPage.more")}
                                onPress={handleNothing}
                                icon={MoreKitIcon}
                                variant="outline"
                            />
                        </Item.Actions>
                    </Item>
                    <Item>
                        <Item.Media
                            alt={t("itemPage.coverAlt")}
                            source={coverSource}
                            variant="image"
                        />
                        <Item.Content>
                            <Item.Title>
                                {t("itemPage.cover")}
                            </Item.Title>
                            <Item.Description>
                                {t("itemPage.coverText")}
                            </Item.Description>
                        </Item.Content>
                        <Item.Actions>
                            <Button
                                title={t("itemPage.download")}
                                onPress={handleNothing}
                            />
                        </Item.Actions>
                    </Item>
                    <Item>
                        <Item.Media
                            avatarProps={{
                                title: t("itemPage.person")
                            }}
                            variant="avatar"
                        />
                        <Item.Content>
                            <Item.Title>
                                {t("itemPage.person")}
                            </Item.Title>
                            <Item.Description>
                                {t("itemPage.personText")}
                            </Item.Description>
                        </Item.Content>
                        <Item.Actions>
                            <Button
                                title={t("itemPage.message")}
                                onPress={handleNothing}
                            />
                        </Item.Actions>
                    </Item>
                    <Item>
                        <Item.Media
                            icon={FileKitIcon}
                            variant="icon"
                        />
                        <Item.Content>
                            <Item.Title>
                                {t("itemPage.report")}
                            </Item.Title>
                            <Item.Description>
                                {t("itemPage.reportText")}
                            </Item.Description>
                        </Item.Content>
                        <Text
                            variant="labelMediumSize"
                            color="mid"
                        >
                            60%
                        </Text>
                        <Item.Footer>
                            <Progress
                                aria-label={t("itemPage.report")}
                                isValueVisible={false}
                                value={60}
                            />
                        </Item.Footer>
                    </Item>
                </Item.Group>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("itemPage.variantTitle")}
            id="variant"
        >
            <Paragraph>
                {t("itemPage.variantText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    VARIANTS.map((variant) => renderSimple(variant, "medium"))
                }
            </Preview>
            <CodeBlock
                code={VARIANT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("itemPage.sizeTitle")}
            id="size"
        >
            <Paragraph>
                {t("itemPage.sizeText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => renderSimple("outline", size))
                }
            </Preview>
            <CodeBlock
                code={SIZE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("itemPage.mediaTitle")}
            id="media"
        >
            <Paragraph>
                {t("itemPage.mediaText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Item
                    variant="outline"
                >
                    <Item.Media
                        icon={BellKitIcon}
                    />
                    <Item.Content>
                        <Item.Title>
                            {t("itemPage.mediaPlain")}
                        </Item.Title>
                    </Item.Content>
                </Item>
                <Item
                    variant="outline"
                >
                    <Item.Media
                        icon={BellKitIcon}
                        variant="icon"
                    />
                    <Item.Content>
                        <Item.Title>
                            {t("itemPage.mediaIcon")}
                        </Item.Title>
                    </Item.Content>
                </Item>
                <Item
                    variant="outline"
                >
                    <Item.Media
                        alt={t("itemPage.coverAlt")}
                        source={coverSource}
                        variant="image"
                    />
                    <Item.Content>
                        <Item.Title>
                            {t("itemPage.mediaImage")}
                        </Item.Title>
                    </Item.Content>
                </Item>
                <Item
                    variant="outline"
                >
                    <Item.Media
                        avatarProps={{
                            title: t("itemPage.person")
                        }}
                        variant="avatar"
                    />
                    <Item.Content>
                        <Item.Title>
                            {t("itemPage.mediaAvatar")}
                        </Item.Title>
                    </Item.Content>
                </Item>
            </Preview>
            <CodeBlock
                code={MEDIA_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("itemPage.contentTitle")}
            id="content"
        >
            <Paragraph>
                {t("itemPage.contentText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Item
                    variant="outline"
                >
                    <Item.Media
                        icon={BellKitIcon}
                        variant="icon"
                    />
                    <Item.Content>
                        <Item.Title>
                            {t("itemPage.notifications")}
                        </Item.Title>
                        <Item.Description>
                            {t("itemPage.notificationsText")}
                        </Item.Description>
                    </Item.Content>
                    <Item.Actions>
                        <Button
                            title={t("itemPage.open")}
                            onPress={handleNothing}
                        />
                    </Item.Actions>
                </Item>
            </Preview>
            <CodeBlock
                code={CONTENT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("itemPage.headerTitle")}
            id="header"
        >
            <Paragraph>
                {t("itemPage.headerText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Item
                    variant="outline"
                >
                    <Item.Header>
                        <Text
                            variant="labelSmallSize"
                            color="mid"
                        >
                            {t("itemPage.shared")}
                        </Text>
                    </Item.Header>
                    <Item.Content>
                        <Item.Title>
                            {t("itemPage.budget")}
                        </Item.Title>
                    </Item.Content>
                    <Item.Footer>
                        <Progress
                            aria-label={t("itemPage.budget")}
                            isValueVisible={false}
                            value={60}
                        />
                    </Item.Footer>
                </Item>
            </Preview>
            <CodeBlock
                code={HEADER_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("itemPage.groupTitle")}
            id="group"
        >
            <Paragraph>
                {t("itemPage.groupText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <View
                    style={[
                        stylesheet.column,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Item.Group
                        variant="outline"
                        isDivided={true}
                        size="small"
                    >
                        {
                            ROWS.map((key) => <Item
                                key={key}
                            >
                                <Item.Media
                                    icon={BellKitIcon}
                                />
                                <Item.Content>
                                    <Item.Title>
                                        {t(`itemPage.${key}`)}
                                    </Item.Title>
                                </Item.Content>
                            </Item>)
                        }
                    </Item.Group>
                    <Item.Group
                        size="small"
                    >
                        {
                            ROWS.map((key) => <Item
                                key={key}
                            >
                                <Item.Content>
                                    <Item.Title>
                                        {t(`itemPage.${key}`)}
                                    </Item.Title>
                                </Item.Content>
                            </Item>)
                        }
                    </Item.Group>
                </View>
            </Preview>
            <CodeBlock
                code={GROUP_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("itemPage.pressTitle")}
            id="press"
        >
            <Paragraph>
                {t("itemPage.pressText")}
            </Paragraph>
            <Preview
                caption={t("itemPage.pressCaption", [
                    t(`itemPage.${selectedKey}`)
                ])}
                isStacked={true}
            >
                <Item.Group
                    variant="outline"
                    isDivided={true}
                >
                    {
                        ROWS.map((key) => <Item
                            onPress={() => setSelectedKey(key)}
                            aria-label={t(`itemPage.${key}`)}
                            isSelected={selectedKey === key}
                            key={key}
                        >
                            <Item.Content>
                                <Item.Title>
                                    {t(`itemPage.${key}`)}
                                </Item.Title>
                                <Item.Description>
                                    {t(`itemPage.${key}Text`)}
                                </Item.Description>
                            </Item.Content>
                        </Item>)
                    }
                </Item.Group>
            </Preview>
            <CodeBlock
                code={PRESS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("itemPage.a11yRole"),
                    t("itemPage.a11yName"),
                    t("itemPage.a11yActions"),
                    t("itemPage.a11ySelected"),
                    t("itemPage.a11yGroup"),
                    t("itemPage.a11yDisabled")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("itemPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "\"plain\" | \"outline\" | \"filled\"",
                        description: t("itemPage.propVariant"),
                        defaultValue: "\"plain\"",
                        name: "variant"
                    },
                    {
                        type: "\"xSmall\" | \"small\" | \"medium\"",
                        description: t("itemPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("itemPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("itemPage.propOnLongPress"),
                        name: "onLongPress",
                        type: "() => void"
                    },
                    {
                        description: t("itemPage.propIsSelected"),
                        defaultValue: "false",
                        name: "isSelected",
                        type: "boolean"
                    },
                    {
                        description: t("itemPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("itemPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("itemPage.propBorderRadius"),
                        name: "borderRadius",
                        type: "number"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("itemPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("itemPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("itemPage.partsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        type: "\"plain\" | \"icon\" | \"image\" | \"avatar\"",
                        description: t("itemPage.propMediaVariant"),
                        defaultValue: "\"plain\"",
                        name: "Media variant"
                    },
                    {
                        description: t("itemPage.propMediaIcon"),
                        type: "NCoreUIKitIcon",
                        name: "Media icon"
                    },
                    {
                        description: t("itemPage.propMediaSource"),
                        type: "ImageSourcePropType",
                        name: "Media source"
                    },
                    {
                        description: t("itemPage.propMediaAvatarProps"),
                        name: "Media avatarProps",
                        type: "IAvatarProps"
                    },
                    {
                        description: t("itemPage.propMediaAlt"),
                        name: "Media alt",
                        type: "string"
                    },
                    {
                        name: "Title, Description numberOfLines",
                        description: t("itemPage.propLines"),
                        defaultValue: "1 | 2",
                        type: "number"
                    },
                    {
                        description: t("itemPage.propGroupVariant"),
                        type: "\"plain\" | \"outline\"",
                        defaultValue: "\"plain\"",
                        name: "Group variant"
                    },
                    {
                        description: t("itemPage.propGroupIsDivided"),
                        name: "Group isDivided",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        type: "\"xSmall\" | \"small\" | \"medium\"",
                        description: t("itemPage.propGroupSize"),
                        defaultValue: "\"medium\"",
                        name: "Group size"
                    },
                    {
                        description: t("itemPage.propSeparatorInset"),
                        name: "Separator inset",
                        type: "number"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
const stylesheet = StyleSheet.create({
    column: {
        flexDirection: "column"
    }
});
export default ItemPage;
